import React from 'react';
import { TrendingUp, Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ChartGradientDefs, chartColorAt, useChartCurrency, useChartGradientIds, useChartPointInteraction } from './chartTheme';

interface TimeData {
  dateLabel: string;
  totalSpend: number;
  tripCount?: number;
  topCategory?: string;
  primaryStore?: string;
}

interface SpendingOverTimeLineChartProps {
  data: TimeData[];
  title?: string;
  currencySymbol?: string;
  activeCurrencySymbol?: string;
}

export const SpendingOverTimeLineChart: React.FC<{ props: SpendingOverTimeLineChartProps; isStreaming?: boolean }> = ({ 
  props,
  isStreaming
}) => {
  const { t, i18n } = useTranslation();
  const { 
    data, 
    title = t('dashboard.spending_over_time'),
  } = props || {};

  const activeCurrencySymbol = useChartCurrency(props);
  const gradientIds = useChartGradientIds();

  const validData = (Array.isArray(data) ? data : []).filter(d => d && typeof d.totalSpend === 'number' && Number.isFinite(d.totalSpend));
  
  const { activePoint: hoveredPoint, pointProps, clearSelection } = useChartPointInteraction(validData.length);

  if (validData.length === 0) {
    if (isStreaming) {
      return (
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm text-center animate-pulse">
           <p className="text-[10px] font-black text-emerald-500 uppercase tracking-widest">{t('dashboard.generating_trends')}</p>
        </div>
      );
    }
    return (
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm text-center">
         <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{t('dashboard.no_trends')}</p>
      </div>
    );
  }

  const maxSpend = Math.max(...validData.map(d => d.totalSpend));
  const minSpend = Math.min(...validData.map(d => d.totalSpend));
  const range = maxSpend - minSpend || 1;
  const padding = range * 0.1;
  const chartMax = maxSpend + padding;
  const chartMin = Math.max(0, minSpend - padding);
  const chartRange = chartMax - chartMin || 1;

  // Path generation for line chart
  const width = 400;
  const height = 220;
  const paddingX = 18;
  const paddingY = 24;
  const chartWidth = width - (paddingX * 2);
  const chartHeight = height - (paddingY * 2);
  
  // A single period sits in the middle instead of pinned to the left edge.
  const step = validData.length > 1 ? chartWidth / (validData.length - 1) : 0;
  const points = validData.map((d, i) => {
    const x = validData.length > 1 ? paddingX + i * step : width / 2;
    const y = paddingY + chartHeight - ((d.totalSpend - chartMin) / chartRange) * chartHeight;
    return { x, y, data: d, color: chartColorAt(validData.length > 1 ? i / (validData.length - 1) : 0) };
  });
  const peakIndex = points.findIndex(p => p.data.totalSpend === maxSpend);
  const labelEvery = Math.max(1, Math.ceil(points.length / 6));

  const pathStr = `M ${points.map(p => `${p.x},${p.y}`).join(' L ')}`;
  const areaPathStr = `M ${points[0].x},${height - paddingY} L ${points.map(p => `${p.x},${p.y}`).join(' L ')} L ${points[points.length - 1].x},${height - paddingY} Z`;

  return (
    <div className="bg-white rounded-[2rem] border border-gray-100 shadow-xl overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="px-4 py-4 sm:px-5 bg-[#1a2b3c] flex flex-wrap gap-2 justify-between items-center text-white">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-emerald-500 rounded-lg shadow-lg shadow-emerald-500/20">
            <TrendingUp size={14} />
          </div>
          <span className="text-[10px] font-black uppercase tracking-widest">{title}</span>
        </div>
        <div className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest opacity-60">
          <Calendar size={10} />
          {t('dashboard.points_count', { count: validData.length })}
        </div>
      </div>

      <div className="p-3 sm:p-4">
        <div className="relative w-full aspect-[20/11] mb-3">
          <svg onPointerDown={event => { if (!(event.target as Element).closest('[data-chart-point]')) clearSelection(); }} viewBox={`0 0 ${width} ${height}`} className="w-full h-full overflow-visible" preserveAspectRatio="xMidYMid meet">
            <ChartGradientDefs ids={gradientIds} x1={paddingX} x2={width - paddingX} top={paddingY} bottom={height - paddingY} />

            {/* Horizontal Grid Lines */}
            {[0, 0.5, 1].map((tick) => {
              const y = paddingY + chartHeight - (tick * chartHeight);
              return (
                <line key={tick} x1={paddingX} y1={y} x2={width - paddingX} y2={y} stroke="#f1f5f9" strokeWidth="1" strokeDasharray="4 4" />
              );
            })}
            
            {points.length > 1 && (
              <>
                <path d={areaPathStr} fill={`url(#${gradientIds.area})`} mask={`url(#${gradientIds.mask})`} />
                <path d={pathStr} fill="none" stroke={`url(#${gradientIds.line})`} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </>
            )}
            
            {/* Interactive Vertical Guide */}
            {hoveredPoint !== null && (
              <line 
                x1={points[hoveredPoint].x} 
                y1={paddingY} 
                x2={points[hoveredPoint].x} 
                y2={height - paddingY} 
                stroke={points[hoveredPoint].color} 
                strokeWidth="1" 
                strokeDasharray="4 4" 
                className="animate-in fade-in duration-300"
              />
            )}

            {/* Data Points and Labels */}
            {points.map((p, i) => (
              <g 
                key={i} 
                className="cursor-pointer outline-none"
                {...pointProps(i, `${p.data.dateLabel}: ${activeCurrencySymbol}${p.data.totalSpend.toFixed(2)}`)}
              >
                {/* Invisible larger hit area for easier tapping/hovering */}
                <circle cx={p.x} cy={p.y} r="12" fill="transparent" />

                {/* Date Label (Static on X-axis), thinned out when crowded */}
                {(i % labelEvery === 0 || i === points.length - 1) && (
                  <text 
                    x={p.x} 
                    y={height - paddingY + 18} 
                    textAnchor={points.length === 1 ? "middle" : i === 0 ? "start" : i === points.length - 1 ? "end" : "middle"}
                    className="fill-gray-400 font-bold text-[8px] uppercase tracking-tighter"
                  >
                    {p.data.dateLabel}
                  </text>
                )}

                {/* The Dot */}
                <circle 
                  cx={p.x} 
                  cy={p.y} 
                  r={hoveredPoint === i ? 6 : 4} 
                  fill={hoveredPoint === i ? p.color : "white"} 
                  stroke={p.color} 
                  strokeWidth="2.5" 
                  className="transition-all duration-200" 
                />
                
                {/* Always visible spending for Peaks/Start/End (unless hovered) */}
                {hoveredPoint === null && (i === 0 || i === points.length - 1 || i === peakIndex) && (
                  <text 
                    x={p.x} 
                    y={p.y - 12} 
                    textAnchor={points.length === 1 ? "middle" : i === 0 ? "start" : i === points.length - 1 ? "end" : "middle"}
                    className="fill-[#1a2b3c] font-black text-[9px]"
                  >
                    {activeCurrencySymbol}{p.data.totalSpend.toFixed(0)}
                  </text>
                )}
              </g>
            ))}
          </svg>

          {/* Floating Rich Tooltip Overlay */}
          {hoveredPoint !== null && (
            <div 
              className="absolute z-20 pointer-events-none bg-[#1a2b3c] text-white p-3 rounded-2xl max-w-[min(15rem,85%)] shadow-2xl border border-white/10 animate-in zoom-in-95 duration-200"
              style={{ 
                left: `${(points[hoveredPoint].x / width) * 100}%`,
                top: `${(points[hoveredPoint].y / height) * 100}%`,
                // Open toward the chart's interior so edge points don't get clipped by the card.
                transform: `translate(${points[hoveredPoint].x < width / 2 ? '-12px' : 'calc(-100% + 12px)'}, ${points[hoveredPoint].y < height * 0.4 ? '12px' : 'calc(-100% - 12px)'})`
              }}
            >
              <div className="space-y-1 min-w-[100px]">
                <p className="text-[8px] font-black text-emerald-400 uppercase tracking-widest">{points[hoveredPoint].data.dateLabel}</p>
                <p className="text-sm font-black leading-none">{activeCurrencySymbol}{points[hoveredPoint].data.totalSpend.toFixed(2)}</p>
                
                <div className="pt-1.5 mt-1.5 border-t border-white/10 space-y-1 empty:hidden">
                  {typeof points[hoveredPoint].data.tripCount === 'number' && (
                    <p className="text-[8px] font-bold text-gray-400 uppercase">{points[hoveredPoint].data.tripCount} {t('chat.visits_label')}</p>
                  )}
                  {points[hoveredPoint].data.topCategory && (
                    <p className="text-[8px] font-bold text-gray-400 uppercase">{t('chat.top_label')}: {points[hoveredPoint].data.topCategory}</p>
                  )}
                  {points[hoveredPoint].data.primaryStore && (
                    <p className="text-[8px] font-bold text-gray-400 uppercase">@ {points[hoveredPoint].data.primaryStore}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-between items-end border-t border-gray-50 pt-3">
           <div>
              <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">{t('dashboard.peak_spend')}</p>
              <p className="text-sm font-black text-[#1a2b3c]">{activeCurrencySymbol}{maxSpend.toFixed(2)}</p>
           </div>
           <div className="text-right">
              <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">{t('dashboard.avg_period')}</p>
              <p className="text-sm font-black text-emerald-500">
                {activeCurrencySymbol}{(validData.reduce((a,b)=>a+b.totalSpend,0)/validData.length).toFixed(2)}
              </p>
           </div>
        </div>
      </div>
    </div>
  );
};
